/**
 * Axios HTTP 客户端配置
 * - 自动携带 JWT Token
 * - 自动处理 401 跳转登录
 * - 统一错误提示
 */
import axios from 'axios';
import { message } from 'antd';

const client = axios.create({
  baseURL: '/api',
  timeout: 15000,
});

// 请求拦截器：自动添加 Authorization Header
client.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // 购物车 session ID（匿名用户）
    const sessionId = localStorage.getItem('sessionId');
    if (sessionId) {
      config.headers['X-Session-Id'] = sessionId;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ---- Token 刷新互斥控制 ----
// 后端刷新是「一次性」的：每次刷新都会删除旧 refresh token 并生成新的。
// 所以并发的多个 401 不能各自去刷新，否则只有第一个成功、其余会拿到已失效的
// refresh token 从而被强制登出。这里用一个「正在刷新」标志 + 等待队列，
// 保证同一时刻只发起一次刷新，其余请求排队等新 token 下发后再重试。
let isRefreshing = false;
let pendingRequests = [];

/** 订阅刷新结果：刷新完成后用新 token（或 null 表示失败）回调 */
function subscribeTokenRefresh(cb) {
  pendingRequests.push(cb);
}

/** 广播刷新结果给所有排队的请求 */
function flushPendingRequests(newToken) {
  pendingRequests.forEach((cb) => cb(newToken));
  pendingRequests = [];
}

/** 清理本地登录态并跳转登录页 */
function forceLogout() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  window.location.href = '/login';
}

// 响应拦截器：处理错误
client.interceptors.response.use(
  (response) => {
    const data = response.data;
    if (data.code !== undefined && data.code !== 200) {
      message.error(data.message || '请求失败');
      return Promise.reject(new Error(data.message));
    }
    return response;
  },
  async (error) => {
    if (!error.response) {
      return Promise.reject(error);
    }

    const { status, data } = error.response;
    const originalRequest = error.config;

    // 非 401 错误：直接提示并抛出
    if (status !== 401) {
      if (status === 403) {
        message.error('无权限访问');
      } else {
        message.error(data?.message || '服务器错误');
      }
      return Promise.reject(error);
    }

    // 401：尝试刷新 token。_retry 标记避免刷新后重试仍 401 时无限循环
    if (originalRequest._retry) {
      forceLogout();
      return Promise.reject(error);
    }
    originalRequest._retry = true;

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      forceLogout();
      return Promise.reject(error);
    }

    // 已有刷新在进行中：排队等待，拿到新 token 后再重试本请求
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((newToken) => {
          if (newToken) {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            resolve(client(originalRequest));
          } else {
            reject(error);
          }
        });
      });
    }

    // 由本请求发起刷新
    isRefreshing = true;
    try {
      const res = await axios.post('/api/auth/refresh', { refreshToken });
      const { accessToken, refreshToken: newRefreshToken } = res.data.data;
      localStorage.setItem('accessToken', accessToken);
      // 关键：后端会轮换 refresh token，必须保存返回的新值，
      // 否则下次刷新仍用旧 token 会失败导致被登出。
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }
      // 唤醒所有排队请求
      flushPendingRequests(accessToken);
      // 重试当前请求
      originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
      return client(originalRequest);
    } catch (refreshError) {
      flushPendingRequests(null);
      forceLogout();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default client;
